import { ChangeDetectionStrategy, Component, effect, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { NgClass, UpperCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { Command, CommandSet } from '@miq/shared-models';
import { ExpandableResource } from '@miq/utils';

const isContainerPresent = ({ isContainer }: ExpandableResource<any>) => isContainer;
const filteredAllowedActions = (commands, { singleObject = false, runningOnRoot = false }) => {
  let allowedCommands = singleObject ? commands : commands.filter(({ isSingleObjectAction }) => !isSingleObjectAction);
  allowedCommands = runningOnRoot
    ? allowedCommands.filter(({ isNestedActionOnly }) => !isNestedActionOnly)
    : allowedCommands;
  return allowedCommands;
};

/**
 * L2-S5: moved verbatim out of `@shared/components/tree-data-table/commands/commands.component`,
 * which is now a re-export shim. Rendered by {@link TreeDataTableComponent} and by six host
 * complex-table components, which keep importing it through the shim.
 *
 * The host version also imported `stringConstants` from `@shared/functions`; the symbol was never
 * referenced in the class, so the dead import is dropped rather than followed into the library.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-commands',
  templateUrl: './commands.component.html',
  styleUrl: './commands.component.scss',
  imports: [NgClass, MatIcon, MatTooltip, UpperCasePipe, L10nTranslatePipe],
})
export class CommandsComponent implements OnInit, OnDestroy {
  resources = input<Array<ExpandableResource<any>>>([]);
  commands = input<CommandSet>({ defaults: [] });
  onErrorCallback = input<Function>(console.error);
  namedEvent = output<string>();
  rowChange = output<void>();
  allowedCommands: Command[];
  protected translationService = inject(L10nTranslationService);
  constructor() {
    effect(() => {
      const resourcesVal = this.resources();
      this.commands();
      if (resourcesVal || this.commands()) {
        if (this.resources().length > 1) {
          this.rowChange.emit();
        }
        this.computeCommands();
      }
    });
  }
  ngOnInit() {
    /* required by label translation feature */
  }
  private computeCommands() {
    const {
      defaults = [],
      mixed: mixedCommands = [],
      leafs: leafsCommands = [],
      containers: containerCommands = [],
    } = this.commands();
    const atLeastOneContainer = this.resources().some(isContainerPresent);
    const atLeastOneLeaf = this.resources().some(resource => !isContainerPresent(resource));
    const isMixedContent = atLeastOneLeaf && atLeastOneContainer;
    const allowedCommands: Command[] = isMixedContent
      ? mixedCommands
      : atLeastOneLeaf && !atLeastOneContainer
        ? [...leafsCommands, ...mixedCommands]
        : !atLeastOneLeaf && atLeastOneContainer
          ? [...containerCommands, ...mixedCommands]
          : defaults;
    const runningOnRoot = this.resources().every(({ level }) => level === 0);
    const allowancesOptions = { singleObject: this.resources().length === 1, runningOnRoot };
    this.allowedCommands = filteredAllowedActions(allowedCommands, allowancesOptions).map(command => ({
      ...command,
      active: false,
      visible: command.isVisible && allowancesOptions.singleObject ? command.isVisible(this.resources()[0]) : true,
      className:
        command.className && typeof command.className === 'function'
          ? command.className(this.resources())
          : command.className,
    }));
  }
  applyCommand(commandItem: Command, icon) {
    if (!commandItem.isDisabled) {
      commandItem.active = true;
      this.applyCommandFlow(commandItem);
    }
  }
  add() {
    // no-op: add action for this table is not yet implemented
  }
  applyActiveCommand(commandItem: Command) {
    commandItem.active = false;
    this.applyCommandFlow({ command: commandItem.onActiveCommand });
  }
  private applyCommandFlow({ command }: { command: string | Function }) {
    if (typeof command === 'string') {
      return this.namedEvent.emit(command);
    }
    try {
      return command(this.resources());
    } catch (e) {
      this.onErrorCallback()(e != null && e.message ? e.message : e);
    }
  }
  ngOnDestroy() {
    /* label translation purpose added */
  }
}
